import { useEffect } from 'react';

import { cn } from '@/utils/cn';

import { useInternalNotifications } from './internal-notifications-store';
import { Notification } from './notification';
import { useNotifications } from './use-notifications';

export type Position =
  | 'left'
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left';

type NotificationsInnerProps = {
  position: Position;
  max: number;
};

const positionCns = {
  left: ['left-0', 'top-1/2', '-translate-y-1/2'],
  'top-left': ['left-0', 'top-0'],
  top: ['left-1/2', 'top-0', '-translate-x-1/2'],
  'top-right': ['right-0', 'top-0'],
  right: ['right-0', 'top-1/2', '-translate-y-1/2'],
  'bottom-right': ['right-0', 'bottom-0'],
  bottom: ['left-1/2', 'bottom-0', '-translate-x-1/2'],
  'bottom-left': ['left-0', 'bottom-0'],
} as const;

const NotificationsInner = ({ position, max }: NotificationsInnerProps) => {
  const { notifications, setMax } = useInternalNotifications();
  const { dismissNotification } = useNotifications();

  useEffect(() => {
    setMax(max);
  }, [max, setMax]);

  return (
    <div
      className={cn(
        positionCns[position],
        ['m-0', 'p-2'],
        'fixed',
        'z-max',
        ['flex', 'flex-col', 'gap-2'],
        ['w-[390px]', 'max-w-full'],
        'list-none',
        'outline-none',
      )}
      role="button"
      tabIndex={0}
      onMouseOver={() =>
        // Pause all active timers
        notifications.active.forEach(n => n.timer.pause())
      }
      onMouseLeave={() =>
        // Resume all timers
        notifications.active.forEach(n => n.timer.resume())
      }
      onFocus={() => {}}
      onBlur={() => {}}
    >
      {/* Show only active notifications */}
      {notifications.active.map(n => (
        <Notification
          key={n.id}
          value={n}
          onClose={() => dismissNotification(n.id)}
        />
      ))}

      {/* Show the number of remaining notifications */}
      {notifications.queued.length > 0 && (
        <div className="flex flex-row items-center justify-end">
          <div
            className={cn(
              ['min-h-12', 'min-w-12'],
              'p-2',
              'bg-red-500',
              ['text-sm', 'font-bold', 'text-white'],
              'rounded-full',
              ['flex', 'flex-row', 'justify-center', 'items-center'],
              'drop-shadow-lg',
            )}
          >
            +{notifications.queued.length}
          </div>
        </div>
      )}
    </div>
  );
};

NotificationsInner.displayName = 'NotificationsInner';

export { NotificationsInner };
